<script setup lang="ts">
import type { CmsElementImageGallery as BaseCmsElementImageGallery } from "@shopware-pwa/composables-next";
import { useCmsElementConfig, useProduct } from "#imports";
import { isSpatial } from "@shopware-pwa/cms-base/helpers/media/isSpatial";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import ProductBasicInfo from "~/components/product/detail/ProductBasicInfo.vue";
import { useWindowSize } from "@vueuse/core";
import type { RefSymbol } from "@vue/reactivity";

// Extend the base type to include product
interface CmsElementImageGallery extends BaseCmsElementImageGallery {
  data: BaseCmsElementImageGallery["data"] & {
    product?: any;
    configuratorSettings?: any[];
  };
}

const props = withDefaults(
  defineProps<{
    content: CmsElementImageGallery;
    slidesToShow?: number;
    slidesToScroll?: number;
  }>(),
  {
    slidesToShow: 5,
    slidesToScroll: 4,
  }
);

const { getConfigValue } = useCmsElementConfig(props.content);

const { product } = useProduct(
  props.content.data?.product,
  props.content.data?.configuratorSettings || []
);

const { medias } = useProductGallery();

const alignment = computed(() => getConfigValue("alignment"));

const speed = ref<number>(300);
const currentIndex = ref(0);
const currentThumb = ref(0);
const imageSlider = useTemplateRef("imageSlider");
const imageThumbsTrack = useTemplateRef("imageThumbsTrack");
const isLoading = ref(true);
const imageThumbsTrackStyle = ref({});
const imageThumbs = useTemplateRef("imageThumbs");
const imageThumbsStyle = ref({});
const mediaGallery = computed(() => [
  ...(props.content.data?.sliderItems ?? []),
  ...medias.value,
]);
const galleryPosition = computed<string>(
  () => getConfigValue("galleryPosition") ?? "left"
);
const scrollPx = ref(0);
const { width } = useWindowSize();
const isMobile = computed(() => width.value < 768);

onMounted(() => {
  initThumbs();
});

function initThumbs() {
  if (imageThumbsTrack.value) {
    setTimeout(() => {
      if (galleryPosition.value === "left") {
        const clientHeight = imageThumbsTrack.value?.clientHeight ?? 0;
        scrollPx.value = clientHeight / mediaGallery.value.length;
        imageThumbsStyle.value = {
          height: `${scrollPx.value * +props.slidesToShow}px`,
        };
      } else {
        const clientWidth = imageThumbs.value?.clientWidth ?? 0;
        scrollPx.value = clientWidth / props.slidesToShow;
        imageThumbsTrackStyle.value = {
          width: `${scrollPx.value * mediaGallery.value.length}px`,
        };
      }
      isLoading.value = false;
    }, 100);
  }
}

function changeCover(i: number) {
  if (i === currentIndex.value) return;
  imageSlider.value?.goToSlide(i);
}

function handleChangeSlide(e: number) {
  currentIndex.value = e;
  if (currentIndex.value > currentThumb.value + props.slidesToShow - 1) {
    move("next", currentIndex.value);
    return;
  }
  if (currentIndex.value < currentThumb.value) {
    move("previous", currentIndex.value);
    return;
  }
}

function move(type: "next" | "previous", specificIndex?: number | string) {
  let step;
  const index =
    typeof specificIndex !== "number"
      ? parseInt(specificIndex as string)
      : specificIndex;
  if (index >= 0) {
    if (type === "next") {
      step =
        index + props.slidesToScroll < mediaGallery.value.length
          ? index
          : mediaGallery.value.length - props.slidesToShow;
    } else {
      step =
        index - props.slidesToScroll > 0 ? index - props.slidesToScroll : 0;
    }
  } else {
    if (type === "next") {
      step =
        currentThumb.value + props.slidesToShow - 1 + props.slidesToScroll <
        mediaGallery.value.length
          ? currentThumb.value + props.slidesToScroll
          : mediaGallery.value.length - props.slidesToShow;
    } else {
      step =
        currentThumb.value - props.slidesToScroll > 0
          ? currentThumb.value - props.slidesToScroll
          : 0;
    }
  }
  currentThumb.value = step;
  let xAxis = 0;
  let yAxis = 0;
  if (galleryPosition.value === "left") {
    yAxis = scrollPx.value * currentThumb.value;
  } else {
    xAxis = scrollPx.value * currentThumb.value;
  }
  imageThumbsTrackStyle.value = {
    ...imageThumbsTrackStyle.value,
    transform: `translate3d(-${xAxis}px, -${yAxis}px, 0px)`,
    transition: `transform ${speed.value}ms ease 0s`,
  };
}

const hasPrevious = computed(() => currentThumb.value > 0);
const hasNext = computed(() => currentThumb.value + props.slidesToShow < mediaGallery.value.length);
function previous() {
  if (currentThumb.value <= 0) {
    return;
  }
  move("previous");
}

function next() {
  if (currentThumb.value + props.slidesToShow >= mediaGallery.value.length) {
    return;
  }
  move("next");
}
</script>

<template>
  <div class="product-gallery-wrap h-auto md:h-full">
    <!-- Product Basic Info - Show only on mobile -->
    <div v-if="isMobile && product" class="md:hidden">
      <ProductBasicInfo :product="product" :alignment="alignment" />
    </div>

    <div
      class="product-image-gallery w-full flex flex-col-reverse md:flex-row flex-wrap md:flex-nowrap md:sticky md:top-[180px]"
    >
      <!-- Product Image Gallery -->
      <div
        class="w-full md:w-[80px] lg:w-[80px]"
        :class="{
          'flex-col-reverse': galleryPosition === 'underneath',
          'opacity-50 pointer-events-none': isLoading,
        }"
      >
        <button
          v-if="mediaGallery.length > slidesToShow"
          class="px-1 bg-transparent ml-[20px] md:block hidden"
          aria-label="Previous image"
          @click="previous"
          :disabled="!hasPrevious"
        >
          <div
            class="h-7 w-7"
            :class="{
              'i-carbon-chevron-up': galleryPosition === 'left',
              'i-carbon-chevron-left': galleryPosition !== 'left',
            }"
          />
        </button>
        <span class="sr-only">Previous image</span>

        <div
          ref="imageThumbs"
          class="overflow-hidden"
          :class="{
            'md:h-[450px]': mediaGallery.length > slidesToShow,
          }"
        >
          <div
            ref="imageThumbsTrack"
            :class="{
              flex: true,
              'thumbs-track flex-row md:flex-col overflow-x-scroll md:overflow-x-hidden':
                galleryPosition === 'left',
            }"
            :style="imageThumbsTrackStyle"
          >
            <div
              v-for="(image, i) in mediaGallery"
              :key="image.media.url"
              :class="{
                'pr-3 md:pb-3 last:pr-0':
                  galleryPosition === 'left',
                'flex-1 px-2.5': galleryPosition === 'underneath',
              }"
            >
              <div
                class="w-20 h-20 overflow-hidden cursor-pointer rounded-md transition duration-150 relative ease-in-out bg-light"
                @click="() => changeCover(i)"
              >
                <span
                  v-if="i === currentIndex"
                  class="absolute inset-0 w-full h-full bg-dark-bg/20"
                />
                <div v-if="isSpatial(image.media)" class="h-full relative">
                  <CmsElementImageGallery3dPlaceholder
                    class="w-full h-full object-center"
                  />
                  <span
                    class="absolute bottom-0 text-sm bg-gray rounded px-1 text-white"
                  >
                    3D</span
                  >
                </div>
                <NuxtImg
                  loading="lazy"
                  provider="ipx"
                  format="webp"
                  :src="image.media.url"
                  class="w-full h-full object-center object-contain"
                  :alt="image.media.alt"
                  :width="image.media.metaData?.width"
                  :height="image.media.metaData?.height"
                />
              </div>
            </div>
          </div>
        </div>
        <button
          v-if="mediaGallery.length > slidesToShow"
          class="px-1 bg-transparent ml-[20px] mt-1 md:block hidden"
          aria-label="Next image"
          @click="next"
          :disabled="!hasNext"
        >
          <span class="sr-only">Next image</span>
          <div
            class="h-7 w-7"
            :class="{
              'i-carbon-chevron-down': galleryPosition === 'left',
              'i-carbon-chevron-right': galleryPosition !== 'left',
            }"
          />
        </button>
      </div>

      <div
        class="w-full md:w-[100%] lg:w-[480px] md:ml-[20px] mb-4 md:mb-0 bg-light rounded-xl max-h-[430px] md:max-h-full overflow-hidden"
      >
        <CommonSwSlider
          ref="imageSlider"
          :config="props.content.config"
          @change-slide="handleChangeSlide"
        >
          <CmsElementImage
            v-for="image of mediaGallery"
            :key="image.media.url"
            :image-gallery="true"
            :content="{ data: image, config: props.content.config } as any"
          />
        </CommonSwSlider>
      </div>
    </div>
  </div>
</template>

<style scoped>
@media (max-width: 768px) {
  .thumbs-track {
    scroll-snap-type: x mandatory;
    scroll-snap-align: center;
    transform: none !important;
    scrollbar-width: none;
  }
  .thumbs-track::-webkit-scrollbar {
    display: none;
  }
}

button:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}
</style>
